import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import {
  BehaviorSubject,
  forkJoin,
  lastValueFrom,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { Acl } from 'src/app/models/acl';
import { ImportReport } from 'src/app/models/gum-models/import-report.model';
import { Property } from 'src/app/models/property';
import { User } from 'src/app/models/user';
import { IimportableService } from './iimportable-service';
import { GumOrganizationsService } from './gum-organizations.service';
import { ServiceBase } from '../service-base';
import { EnumUserAccessLevels } from 'src/app/models/enum-user-access-levels';
import { EnumOrganizationAccessLevels } from 'src/app/models/enum-organization-access-levels';

@Injectable({
  providedIn: 'root',
})
export class GumUsersService extends ServiceBase implements IimportableService {
  public currentUserSubject: BehaviorSubject<User | null>;

  public get currentUserChange$() {
    return this.currentUserSubject.asObservable();
  }

  public devAccount = false;
  private importFinished = new Subject<ImportReport>();
  private importBatch: number = 10;

  public get onImportFinished$() {
    return this.importFinished.asObservable();
  }

  constructor(
    private http: HttpClient,
    private organizationsServices: GumOrganizationsService
  ) {
    super();
    const userJson = sessionStorage.getItem('user');
    const userParsed = userJson ? (JSON.parse(userJson) as User) : null;
    this.currentUserSubject = new BehaviorSubject<User | null>(userParsed);
  }

  public getSessionCurrentUser(): Observable<User | null> {
    return this.currentUserSubject.pipe(first());
  }

  public isDevAccount(): boolean {
    return this.devAccount;
  }

  private selectedUser: User | null = null;

  setSelectedUser(user: User): void {
    this.selectedUser = user;
  }

  getSelectedUser(): User | null {
    return this.selectedUser;
  }

  clearSelectedUser(): void {
    this.selectedUser = null;
  }

  public getCurrentUserSync(): User | null {
    return this.currentUserSubject.value;
  }

  getCurrentUser(): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http.get<User>(`${this.apiUrl}/api/gum/user`, httpOptions).pipe(
      tap((user: User) => {
        sessionStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
        this.devAccount = user.id === 'c569a4b4eae74f99b31ca00f7b7de444';
      })
    );
  }

  public checkCurrentUser(accessToken: string): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Authorization', accessToken)
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    return this.http.get<User>(`${this.apiUrl}/api/gum/user`, httpOptions).pipe(
      tap((user: User) => {
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('SessionId', Guid.create().toString());
        this.currentUserSubject.next(user);
        this.devAccount = user.id === 'c569a4b4eae74f99b31ca00f7b7de444';
      })
    );
  }

  getUser(userId: string): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http
      .get<User>(`${this.apiUrl}/api/gum/users/${userId}`, httpOptions)
      .pipe(
        tap((user) => {
          console.log(
            '[GumUsersService] getUser → id:',
            user.id,
            'account:',
            user.account,
            'organizationId:',
            user.organizationId,
            'organizationPath:',
            user.organizationPath
          );
        })
      );
  }

  getUserWithDisplayParentOrganization(
    userId: string,
    executeOption: boolean = true
  ): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        !executeOption
          ? ''
          : this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http
      .get<User>(`${this.apiUrl}/api/gum/users/${userId}`, httpOptions)
      .pipe(
        tap((user) => {
          console.log(
            '[GumUsersService] getUserWithDisplayParentOrganization → id:',
            user.id,
            'account:',
            user.account,
            'organizationId:',
            user.organizationId,
            'organizationPath:',
            user.organizationPath
          );
        }),
        switchMap((user) =>
          this.organizationsServices.getOrganisation(user.organizationId).pipe(
            map((organization) => {
              user.displayParentOrganization = organization.display;
              return user;
            })
          )
        )
      );
  }

  getUsersWithDisplayParentOrganization(): Observable<User[]> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return forkJoin([
      this.http.get<User[]>(`${this.apiUrl}/api/gum/users`, httpOptions),
      this.organizationsServices.getOrganisations(),
    ]).pipe(
      map(([users, organizations]) => {
        users.forEach((user) => {
          const displayParentOrg = organizations.find(
            (x) => x.id === user.organizationId
          );
          user.displayParentOrganization = displayParentOrg
            ? displayParentOrg.display
            : '';
        });
        return users;
      })
    );
  }

  getUsers(): Observable<User[]> {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http.get<User[]>(`${this.apiUrl}/api/gum/users`, httpOptions);
  }

  addUser(user: User): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    return this.http.post<User>(
      `${this.apiUrl}/api/gum/users`,
      JSON.stringify(user),
      httpOptions
    );
  }

  addUsers(users: User[], globalNotification = true): Observable<ImportReport> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('global-notification', String(globalNotification))
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    return this.http.post<ImportReport>(
      `${this.apiUrl}/api/gum/users/import`,
      JSON.stringify(users),
      httpOptions
    );
  }

  deleteUser(userId: string) {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http.delete(
      `${this.apiUrl}/api/gum/users/${userId}`,
      httpOptions
    );
  }

  deleteUserProperty(userId: string, propertyName: string) {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http.delete(
      `${this.apiUrl}/api/gum/users/${userId}/properties/${propertyName}`,
      httpOptions
    );
  }

  deleteUserRight(userId: string, rightName: string) {
    const httpOptions = {
      headers: new HttpHeaders().set(
        'X-OrganizationId',
        this.organizationsServices.getCurrentOrganization()?.id ?? ''
      ),
    };

    return this.http.delete(
      `${this.apiUrl}/api/gum/users/${userId}/rights/${rightName}`,
      httpOptions
    );
  }

  updateUser(user: User): Observable<User> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    return this.http.patch<User>(
      `${this.apiUrl}/api/gum/users/${user.id}`,
      user,
      httpOptions
    );
  }

  updateUserProperty(
    userId: string,
    propertyName: string,
    propertyValue: string
  ): Observable<Property> {
    // Skip si valeur vide / null / undefined
    if (
      propertyValue === null ||
      propertyValue === undefined ||
      propertyValue === ''
    ) {
      console.debug(
        `[SKIP] Property '${propertyName}' is empty, skipping update for user '${userId}'`
      );
      return of(); // aucun appel réseau, évite l'erreur backend
    }

    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    // Envoi correct au format JSON attendu par le backend
    return this.http.put<Property>(
      `${this.apiUrl}/api/gum/users/${userId}/properties/${propertyName}`,
      { value: propertyValue }, // <--- C’est cette ligne qui corrige le 415
      httpOptions
    );
  }

  updateUserRight(
    userId: string,
    rightName: string,
    rightValue: number
  ): Observable<Property> {
    const httpOptions = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set(
          'X-OrganizationId',
          this.organizationsServices.getCurrentOrganization()?.id ?? ''
        ),
    };

    return this.http.put<Property>(
      `${this.apiUrl}/api/gum/users/${userId}/rights/${rightName}`,
      rightValue,
      httpOptions
    );
  }

  removeCurrentUser() {
    sessionStorage.clear();
    this.currentUserSubject?.next(null);
    this.organizationsServices.removeCurrentOrganization();
  }

  async import(datas: any, properties: any, rights: any) {
    let usersToAdd: User[] = [];
    try {
      const datasToAdd = datas as User[];

      let propertiesUsers: { user: string; name: string; value: string }[];
      let rightsUsers: { user: string; name: string; value: number }[];

      if (properties) {
        propertiesUsers = properties;
      }

      if (rights) {
        rightsUsers = rights;
      }

      datasToAdd.forEach((user) => {
        const newUser = new User();
        newUser.organizationId =
          this.organizationsServices.getCurrentOrganization()?.id ?? '';
        newUser.account = user.account;
        newUser.display = user.display?.trim() ?? '';
        newUser.appData = user.appData?.trim() ?? '';
        newUser.organizationAccessLevel =
          user.organizationAccessLevel ?? EnumOrganizationAccessLevels.None;
        newUser.userAccessLevel =
          user.userAccessLevel ?? EnumUserAccessLevels.None;
        newUser.networks = user.networks?.trim() ?? '';
        newUser.enabled = Boolean(JSON.parse(String(user.enabled ?? 'true')));

        if (propertiesUsers) {
          newUser.properties = propertiesUsers
            .filter((p) => p.user === user.display)
            .map((a) => {
              const property = new Property();
              property.name = a.name;
              property.value = a.value?.trim() ?? '';
              return property;
            });
        }

        if (rightsUsers) {
          newUser.rights = rightsUsers
            .filter((r) => r.user === user.display)
            .map((a) => {
              const right = new Acl();
              right.name = a.name.trim();
              right.value = a.value;
              return right;
            });
        }

        usersToAdd.push(newUser);
      });

      const importReport = new ImportReport();
      importReport.progress.total = usersToAdd.length;

      this.importBatch = await lastValueFrom(this.getImportBatch());

      for (let i = 0; i < usersToAdd.length; i += this.importBatch) {
        const chunk = usersToAdd.slice(i, i + this.importBatch);
        try {
          const report = await lastValueFrom(this.addUsers(chunk, false));
          importReport.errorsReport = importReport.errorsReport.concat(
            report.errorsReport
          );
          importReport.progress.completed += chunk.length;
          importReport.isFinished =
            importReport.progress.completed === importReport.progress.total;
          this.importFinished.next(importReport);
        } catch {
          importReport.isImportError = true;
          importReport.isFinished = true;
          this.importFinished.next(importReport);
          break;
        }
      }
    } catch {
      const fileError = new ImportReport();
      fileError.isFileTreatmentError = true;
      this.importFinished.next(fileError);
    }
  }

  private getImportBatch(): Observable<number> {
    return this.http.get<number>(
      `${this.apiUrl}/api/gum/users/import/batchsetting`
    );
  }
}
